import { useCallback, useEffect, useState } from 'react';
import { demeterApi } from '../../../../Apis/Apis';
import { getUserDataPreferences } from '../../../../Components/UserPreference/UserPreference';
import { DemeterDashboardPresetChartModel, DemeterLeadingIndicatorModel, DemeterMarket, DemeterWorkerType } from '../../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { MarketPriceModelBySymbol, selectMarketPricesBySymbol } from '../../../../Redux/Slices/MarketPricesSlice';
import { selectLocation } from '../../../../Redux/Slices/SystemSlice';
import useLightstreamerMarketPrices, { IndicatorsWidgetInfoInterface } from '../../../Core/Hooks/useLightstreamerMarketPricesHook';
import formattingService from '../../../Services/Formatting/FormattingService';

interface GlobalLeadingIndicatorsState {
    [market: string]: {
        allIndicators: DemeterLeadingIndicatorModel[];
        userSelectedIndicators: DemeterLeadingIndicatorModel[];
        dashboardPresets: DemeterDashboardPresetChartModel[];
        userPreferenceIndicatorGuids: string[];
        indicatorsWidgetInfo: IndicatorsWidgetInfoInterface[];
        isLoading: boolean;
        lastUpdated: number;
    };
}

interface UseGlobalLeadingIndicatorsReturn {
    initializeGlobalData: (markets: DemeterMarket[]) => Promise<void>;
    getIndicatorsForMarket: (market: DemeterMarket) => {
        allIndicators: DemeterLeadingIndicatorModel[];
        userSelectedIndicators: DemeterLeadingIndicatorModel[];
        dashboardPresets: DemeterDashboardPresetChartModel[];
        userPreferenceIndicatorGuids: string[];
        indicatorsWidgetInfo: IndicatorsWidgetInfoInterface[];
        isLoading: boolean;
        updateIndicators: (updatedIndicators: DemeterLeadingIndicatorModel[]) => Promise<void>;
    };
    isGlobalDataInitialized: boolean;
}

const defaultWorkerType = DemeterWorkerType.Farmer;

let globalLeadingIndicatorsState: GlobalLeadingIndicatorsState = {};
const globalStateSubscribers: Set<() => void> = new Set();
let isGlobalInitialized = false;

const notifySubscribers = () => {
    globalStateSubscribers.forEach((callback) => callback());
};

const updateGlobalState = (newState: GlobalLeadingIndicatorsState) => {
    globalLeadingIndicatorsState = newState;
    notifySubscribers();
};

const useGlobalLeadingIndicators = (): UseGlobalLeadingIndicatorsReturn => {
    const [, forceUpdate] = useState({});
    const location = useApplicationSelector(selectLocation);
    const marketPricesBySymbol = useApplicationSelector(selectMarketPricesBySymbol);
    const { updateSubscription } = useLightstreamerMarketPrices();

    const createEmptyMarketState = useCallback(
        (isLoading = false) => ({
            allIndicators: [],
            userSelectedIndicators: [],
            dashboardPresets: [],
            userPreferenceIndicatorGuids: [],
            indicatorsWidgetInfo: [],
            isLoading,
            lastUpdated: Date.now(),
        }),
        [],
    );

    const getAllReutersInstrumentCodes = useCallback(
        (excludeMarket?: DemeterMarket) =>
            Object.entries(globalLeadingIndicatorsState)
                .filter(([market]) => market !== excludeMarket)
                .flatMap(([, marketData]) => marketData.userSelectedIndicators)
                .map((indicator) => indicator.reutersInstrumentCode)
                .filter((code): code is string => code != null),
        [],
    );

    useEffect(() => {
        const updateCallback = () => forceUpdate({});
        globalStateSubscribers.add(updateCallback);

        return () => {
            globalStateSubscribers.delete(updateCallback);
        };
    }, []);

    const processMarketResults = useCallback(
        (marketResults: PromiseSettledResult<any>[], markets: DemeterMarket[], newState: GlobalLeadingIndicatorsState) => {
            let hasAnySuccess = false;
            marketResults.forEach((result, index) => {
                const market = markets[index];
                if (result.status === 'fulfilled' && result.value.success) {
                    newState[market] = result.value.data;
                    hasAnySuccess = true;
                } else {
                    const errorData = result.status === 'fulfilled' ? result.value.data : createEmptyMarketState(false);
                    newState[market] = errorData;
                }
            });

            return hasAnySuccess;
        },
        [createEmptyMarketState],
    );

    const transformIndicatorsData = useCallback(
        (indicators: DemeterLeadingIndicatorModel[], marketPrices: MarketPriceModelBySymbol): IndicatorsWidgetInfoInterface[] =>
            indicators.map((indicator) => {
                const marketPrice = indicator.reutersInstrumentCode && marketPrices ? marketPrices[indicator.reutersInstrumentCode] : null;

                let value = indicator.currentValue;
                let isFuturesSettlement = false;

                if (marketPrice?.latestPrice && indicator.priceMultiplierFutures) {
                    value = marketPrice.latestPrice * indicator.priceMultiplierFutures;
                } else if (marketPrice?.settlementPrice && indicator.priceMultiplierFutures) {
                    value = marketPrice.settlementPrice * indicator.priceMultiplierFutures;
                    isFuturesSettlement = true;
                }

                let { previousValue } = indicator;
                if (indicator.reutersInstrumentCode) {
                    previousValue = marketPrice?.settlementPrice ? marketPrice.settlementPrice * (indicator.priceMultiplierFutures ?? 1) : undefined;
                }

                const latestPriceAsOfDate =
                    indicator.reutersInstrumentCode && marketPrices[indicator.reutersInstrumentCode!]?.latestPriceAsOfDate
                        ? formattingService.toBrowserDateAndTimeFromUTC(marketPrices[indicator.reutersInstrumentCode!].latestPriceAsOfDate!)
                        : undefined;

                const settlementPriceAsOfDate =
                    indicator.reutersInstrumentCode && marketPrices[indicator.reutersInstrumentCode!]?.settlementPriceAsOfDate
                        ? formattingService.toBrowserDateAndTimeFromUTC(marketPrices[indicator.reutersInstrumentCode!].settlementPriceAsOfDate!)
                        : undefined;

                return {
                    asOfDate: indicator.asOfDate,
                    latestPriceAsOfDate,
                    settlementPriceAsOfDate,
                    title: indicator.displayName,
                    change: marketPrice?.percentChange ? marketPrice.percentChange : indicator.percentChange || 0,
                    currency: indicator.currency as string,
                    reutersInstrumentCode: indicator.reutersInstrumentCode,
                    commodity: indicator.commodity,
                    region: indicator.region,
                    value,
                    isFuturesSettlement,
                    displayDecimalPlacesMinimum: indicator.displayDecimalPlacesMinimum,
                    displayDecimalPlacesMaximum: indicator.displayDecimalPlacesMaximum,
                    indicatorModel: {
                        ...indicator,
                        dataFrequency: indicator.priceFrequency,
                        asOfDate: indicator.asOfDate,
                        previousValue,
                    },
                };
            }),
        [],
    );

    useEffect(() => {
        if (!Object.keys(globalLeadingIndicatorsState).length) {
            return;
        }

        const updatedState = { ...globalLeadingIndicatorsState };
        let hasChanges = false;

        Object.keys(updatedState).forEach((market) => {
            const currentData = updatedState[market];
            const newIndicatorsWidgetInfo = transformIndicatorsData(currentData.userSelectedIndicators, marketPricesBySymbol);

            if (
                newIndicatorsWidgetInfo.length !== currentData.indicatorsWidgetInfo.length ||
                newIndicatorsWidgetInfo.some(
                    (item, index) =>
                        item.value !== currentData.indicatorsWidgetInfo[index]?.value || item.change !== currentData.indicatorsWidgetInfo[index]?.change,
                )
            ) {
                updatedState[market] = {
                    ...currentData,
                    indicatorsWidgetInfo: newIndicatorsWidgetInfo,
                    lastUpdated: Date.now(),
                };
                hasChanges = true;
            }
        });

        if (hasChanges) {
            updateGlobalState(updatedState);
        }
    }, [marketPricesBySymbol, transformIndicatorsData]);

    const initializeGlobalData = useCallback(
        async (markets: DemeterMarket[]) => {
            if (isGlobalInitialized || !markets.length || !location) {
                return;
            }

            const newState: GlobalLeadingIndicatorsState = {};

            markets.forEach((market) => {
                newState[market] = createEmptyMarketState(true);
            });

            updateGlobalState(newState);

            try {
                const userWorkerType = ((await getUserDataPreferences('workerType')) as DemeterWorkerType) ?? defaultWorkerType;

                if (!location || !userWorkerType) {
                    throw new Error(`Missing required parameters: location=${location}, workerType=${userWorkerType}`);
                }

                const marketResults = await Promise.allSettled(
                    markets.map(async (market) => {
                        try {
                            const [allIndicatorsResponse, userSelectedResponse, presetsResponse] = await Promise.all([
                                demeterApi.listDemeterLeadingIndicators(market, true, false),
                                demeterApi.listDemeterLeadingIndicators(market, false, true),
                                demeterApi.getDashboardPresets(market, location, undefined, userWorkerType),
                            ]);

                            const userSelectedIndicators = userSelectedResponse.data.rows || [];
                            const indicatorsWidgetInfo = transformIndicatorsData(userSelectedIndicators, marketPricesBySymbol);

                            return {
                                market,
                                success: true,
                                data: {
                                    allIndicators: allIndicatorsResponse.data.rows || [],
                                    userSelectedIndicators,
                                    dashboardPresets: presetsResponse.data.dashboardPreset?.dashboardPresetsCharts || [],
                                    userPreferenceIndicatorGuids: userSelectedIndicators.map(
                                        (ind: DemeterLeadingIndicatorModel) => ind.demeterLeadingIndicatorGuid,
                                    ),
                                    indicatorsWidgetInfo,
                                    isLoading: false,
                                    lastUpdated: Date.now(),
                                },
                            };
                        } catch (marketError) {
                            return {
                                market,
                                success: false,
                                error: marketError,
                                data: {
                                    allIndicators: [],
                                    userSelectedIndicators: [],
                                    dashboardPresets: [],
                                    userPreferenceIndicatorGuids: [],
                                    indicatorsWidgetInfo: [],
                                    isLoading: false,
                                    lastUpdated: Date.now(),
                                },
                            };
                        }
                    }),
                );

                const hasAnySuccess = processMarketResults(marketResults, markets, newState);

                if (hasAnySuccess) {
                    const allReutersInstrumentCodes = Object.values(newState)
                        .filter((marketData) => marketData.userSelectedIndicators.length > 0)
                        .flatMap((marketData) => marketData.userSelectedIndicators)
                        .map((indicator) => indicator.reutersInstrumentCode)
                        .filter((code) => code != null) as string[];

                    if (allReutersInstrumentCodes.length > 0) {
                        updateSubscription(allReutersInstrumentCodes);
                    }
                }

                updateGlobalState(newState);
                isGlobalInitialized = true;
            } catch (error) {
                markets.forEach((market) => {
                    newState[market] = createEmptyMarketState(false);
                });
                updateGlobalState(newState);
            }
        },
        [location, marketPricesBySymbol, updateSubscription, transformIndicatorsData, createEmptyMarketState, processMarketResults],
    );

    const getIndicatorsForMarket = useCallback(
        (market: DemeterMarket) => {
            const marketData = globalLeadingIndicatorsState[market] || createEmptyMarketState(true);

            const updateIndicators = async (updatedIndicators: DemeterLeadingIndicatorModel[]) => {
                try {
                    await demeterApi.batchUpdateDemeterLeadingIndicators({
                        demeterLeadingIndicatorGuids: updatedIndicators.map((x) => x.demeterLeadingIndicatorGuid),
                    });

                    const freshDataResponse = await demeterApi.listDemeterLeadingIndicators(market, false, true);
                    const freshUserSelectedIndicators =
                        freshDataResponse.data.rows?.filter((indicator: DemeterLeadingIndicatorModel) =>
                            updatedIndicators.some((updated) => updated.demeterLeadingIndicatorGuid === indicator.demeterLeadingIndicatorGuid),
                        ) || [];

                    const newReutersInstrumentCodes = freshUserSelectedIndicators
                        .map((indicator: DemeterLeadingIndicatorModel) => indicator.reutersInstrumentCode)
                        .filter((code): code is string => code != null);

                    const allReutersInstrumentCodes = [...getAllReutersInstrumentCodes(market), ...newReutersInstrumentCodes];

                    updateSubscription(allReutersInstrumentCodes);

                    const indicatorsWidgetInfo = transformIndicatorsData(freshUserSelectedIndicators, marketPricesBySymbol);

                    const newState = {
                        ...globalLeadingIndicatorsState,
                        [market]: {
                            ...globalLeadingIndicatorsState[market],
                            userSelectedIndicators: freshUserSelectedIndicators,
                            userPreferenceIndicatorGuids: freshUserSelectedIndicators.map(
                                (ind: DemeterLeadingIndicatorModel) => ind.demeterLeadingIndicatorGuid,
                            ),
                            indicatorsWidgetInfo,
                            lastUpdated: Date.now(),
                        },
                    };

                    updateGlobalState(newState);
                } catch {
                    // eslint-disable-next-line no-console
                    console.error('Failed to update leading indicators');
                }
            };

            return {
                ...marketData,
                updateIndicators,
            };
        },
        [marketPricesBySymbol, updateSubscription, transformIndicatorsData, createEmptyMarketState, getAllReutersInstrumentCodes],
    );

    return {
        initializeGlobalData,
        getIndicatorsForMarket,
        isGlobalDataInitialized: isGlobalInitialized,
    };
};

export default useGlobalLeadingIndicators;
